import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, startWith } from 'rxjs';
import { APP_REGISTRY_TOKEN, AppDefinition, AppKey, AppSection, findApp } from '@/core/layout/app-registry';
import { ADMIN_AREA_LABEL, ADMIN_REGISTRY_TOKEN, AdminGroupDefinition, AdminGroupKey, findAdminGroup } from '@/core/layout/admin-registry';
import { resolveActiveSection, stripQueryAndFragment } from '@/core/layout/active-section';
import { MastheadContext } from '@/core/layout/masthead-context.service';
import { isMenuItemVisible } from '@/core/layout/menu-visibility';
import { selectUserProfile, selectUserTeams } from '@/store/current-user/current-user.selectors';
import { UserTeam } from '@/core/admin/models/team.model';
import { AppMotifComponent } from '../app-motif/app-motif.component';
import { AppMastheadApp, AppMastheadComponent, AppMastheadTab, AppMastheadTone } from './app-masthead.component';

/**
 * What the current route says: the app (`data.app`) or the Administration
 * group (`data.adminGroup`) it belongs to, and the URL path (no query/fragment).
 */
type RouteState =
  | { readonly kind: 'app'; readonly app: AppDefinition; readonly path: string }
  | { readonly kind: 'admin'; readonly group: AdminGroupDefinition; readonly path: string };

/** The route marker found on the trail, before the registry lookup. */
type RouteMarker = { readonly kind: 'app'; readonly key: AppKey } | { readonly kind: 'admin'; readonly key: AdminGroupKey };

interface MastheadViewModel {
  readonly tone: AppMastheadTone;
  readonly app: AppMastheadApp;
  /** Which motif to draw: the app's key, or `null` in the admin tone (no motif is part of that register). */
  readonly motif: AppKey | null;
  readonly tabs: readonly AppMastheadTab[];
  readonly activeKey: string;
}

/**
 * **App masthead container** (SCRUM-593, masthead epic story 6; Administration
 * in SCRUM-621, admin masthead epic story 3). Lives in the layout shell under
 * the breadcrumb and renders {@link AppMastheadComponent} for the app or the
 * Administration group the current route belongs to, or nothing at all.
 *
 * - On construction and on every `NavigationEnd` it reads the deepest
 *   activated route carrying `data.app` (see `AppRouteData`) or
 *   `data.adminGroup` (see `AdminRouteData`), and looks the app up in the app
 *   registry or the group up in the admin registry. Routes with neither
 *   marker (My Workspace, profile, auth) render nothing.
 * - An app renders in the `app` tone with its motif. An admin group renders
 *   in the `admin` tone: the gear glyph, the group label as the `h1`, the
 *   constant "Administration" eyebrow, the group's pages as tabs and no motif.
 * - Sections and pages are gated with the same helper and the same store
 *   selectors as the sidebar (`app.menu.ts`). The app- and group-level gates
 *   (roles, WM-enabled team) are enforced by the route guards, so whoever
 *   reached the page gets the masthead.
 * - The active tab follows `resolveActiveSection`: a page-declared section on
 *   {@link MastheadContext}, else the longest matching URL prefix, else the
 *   first section or page. The context resets itself on every
 *   `NavigationEnd`, before the new page's components initialise.
 *
 * Nothing is projected into `[masthead-action]` yet.
 */
@Component({
  standalone: true,
  selector: 'app-masthead-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppMastheadComponent, AppMotifComponent],
  template: `
    @if (vm(); as vm) {
      <app-masthead [tone]="vm.tone" [app]="vm.app" [tabs]="vm.tabs" [activeTabKey]="vm.activeKey">
        @if (vm.motif; as motif) {
          <app-motif masthead-motif [app]="motif" />
        }
      </app-masthead>
    }
  `,
  styles: [':host { display: block; }'],
})
export class AppMastheadContainer {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly registry = inject(APP_REGISTRY_TOKEN);
  private readonly adminRegistry = inject(ADMIN_REGISTRY_TOKEN);
  private readonly context = inject(MastheadContext);

  /** Re-read on every `NavigationEnd`. */
  private readonly route = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.readRoute(event.urlAfterRedirects)),
      startWith(this.readRoute(this.router.url)),
    ),
    { requireSync: true },
  );

  /** Same source as the sidebar: `user.roles[].type`. */
  private readonly roles = toSignal(
    this.store.select(selectUserProfile).pipe(
      map((user): readonly string[] => user?.roles?.map((role: { type: string }) => role.type) ?? []),
    ),
    { initialValue: [] as readonly string[] },
  );

  /** Same source as the sidebar's WM data gate (loaded there via `loadCurrentUserTeams`). */
  private readonly hasWmEnabledTeam = toSignal(
    this.store
      .select(selectUserTeams)
      .pipe(map((teams: UserTeam[] | null | undefined) => (teams ?? []).some((t) => t.workspaceManagementEnabled === true))),
    { initialValue: false },
  );

  protected readonly vm = computed<MastheadViewModel | null>(() => {
    const route = this.route();
    if (!route) {
      return null;
    }
    const ctx = { roles: this.roles(), hasWmEnabledTeam: this.hasWmEnabledTeam() };
    const declared = this.context.section();
    const build = (all: readonly AppSection[]): Pick<MastheadViewModel, 'tabs' | 'activeKey'> => {
      const visible = all.filter((section) => isMenuItemVisible(section, ctx));
      return {
        tabs: visible.map(({ key, label, link }) => ({ key, label, link })),
        activeKey: resolveActiveSection(visible, route.path, declared) ?? '',
      };
    };

    if (route.kind === 'admin') {
      return {
        tone: 'admin',
        app: { key: 'admin', name: route.group.label, eyebrow: ADMIN_AREA_LABEL },
        motif: null,
        ...build(route.group.pages),
      };
    }
    const { key, name, purpose } = route.app;
    return { tone: 'app', app: { key, name, purpose }, motif: key, ...build(route.app.sections) };
  });

  /**
   * The deepest activated route (primary outlet) that carries `data.app` or
   * `data.adminGroup`, or `null` when none does. The deeper marker wins if a
   * trail ever carried both.
   */
  private readRoute(url: string): RouteState | null {
    let marker: RouteMarker | undefined;
    for (
      let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
      snapshot;
      snapshot = snapshot.firstChild
    ) {
      if (snapshot.data['app']) {
        marker = { kind: 'app', key: snapshot.data['app'] as AppKey };
      } else if (snapshot.data['adminGroup']) {
        marker = { kind: 'admin', key: snapshot.data['adminGroup'] as AdminGroupKey };
      }
    }
    if (!marker) {
      return null;
    }
    const path = stripQueryAndFragment(url);
    return marker.kind === 'admin'
      ? { kind: 'admin', group: findAdminGroup(marker.key, this.adminRegistry), path }
      : { kind: 'app', app: findApp(marker.key, this.registry), path };
  }
}
